/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  FileX,
  FolderOpen,
  Link2,
  Loader2,
  Lock,
  ShieldAlert,
  Upload,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Text,
} from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks';
import { MobileBottomSheet } from '@/shared/ui/components/composed/mobile-sheet';

import { LaunchesService } from '../../services/launchesApi';
import type { EmbedStatus, MaterialCategory, ResolvedLink } from '../../types';

interface AddMaterialSheetProps {
  open: boolean;
  agencyId: string;
  launchId: string;
  categories: MaterialCategory[];
  onClose: () => void;
  onCreated: () => void;
}

/** Mirrors MAX_MATERIAL_BYTES on the server: 50 MiB. */
const MAX_UPLOAD_BYTES = 52428800;

/** Icon for each way a link can fail. */
const FAILURE_ICON: Record<Exclude<EmbedStatus, 'ok'>, typeof Lock> = {
  private: Lock,
  removed: FileX,
  domain_not_allowed: ShieldAlert,
  folder_not_supported: FolderOpen,
};

/**
 * What the pasted link turned out to be — recognised, or refused with a
 * reason the user can act on.
 */
function LinkOutcome({ resolved }: { resolved: ResolvedLink }) {
  const { t } = useTranslation('features/launches');

  if (resolved.embed_status === 'ok') {
    return (
      <div className="flex flex-col gap-[var(--space-1)] rounded-(--radius-md) border border-[var(--border-subtle)] p-[var(--space-3)]">
        <Text variant="small" as="span" className="[font-weight:var(--font-semibold)] text-[var(--text-primary)]">
          {t('materials.addSheet.recognised', {
            provider: t(`materials.providers.${resolved.provider ?? 'drive'}`),
          })}
        </Text>
        {resolved.provider_title && (
          <Text variant="meta" as="span" className="truncate text-[var(--text-secondary)]">
            {resolved.provider_title}
          </Text>
        )}
        <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
          {t('materials.addSheet.staysOnProvider', {
            provider: t(`materials.providers.${resolved.provider ?? 'drive'}`),
          })}
        </Text>
      </div>
    );
  }

  const Icon = FAILURE_ICON[resolved.embed_status];
  return (
    <div
      className="flex gap-[var(--space-3)] rounded-(--radius-md) p-[var(--space-3)]"
      style={{
        background: 'var(--launch-material-broken-bg)',
        border: '1px solid var(--launch-material-broken-border)',
        color: 'var(--launch-material-broken-fg)',
      }}
    >
      <Icon size={18} />
      <div className="flex flex-col gap-[var(--space-1)]">
        <Text variant="small" as="span" className="[font-weight:var(--font-semibold)]">
          {t(`materials.addSheet.failure.${resolved.embed_status}.title`)}
        </Text>
        <Text variant="meta" as="span">
          {t(`materials.addSheet.failure.${resolved.embed_status}.body`)}
        </Text>
      </div>
    </div>
  );
}

/**
 * Publish a material: paste a link, or upload a file.
 *
 * The link is resolved before anything is stored, which is what lets a
 * private or deleted link be caught while the user is still looking at the
 * field — rather than becoming a broken row in the library.
 */
export function AddMaterialSheet({
  open,
  agencyId,
  launchId,
  categories,
  onClose,
  onCreated,
}: AddMaterialSheetProps) {
  const { t } = useTranslation('features/launches');
  const isMobile = useIsMobile();
  const [url, setUrl] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState<ResolvedLink | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'link' | 'file'>('link');
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const reset = () => {
    setUrl(''); setResolved(null); setTitle(''); setCategory(''); setResolving(false);
    setFile(null); setUploadError(null); setMode('link');
  };

  /**
   * Accepts a file for upload, refusing video before anything is
   * registered: video is embedded from a provider, and a 200MB stand tour
   * would fail after the upload, not before it.
   */
  const pickFile = (picked: File | null) => {
    setUploadError(null);
    if (!picked) { setFile(null); return; }
    if (picked.type.startsWith('video/')) {
      setUploadError(t('materials.addSheet.noVideoUpload'));
      setFile(null);
      return;
    }
    if (picked.size > MAX_UPLOAD_BYTES) {
      setUploadError(t('materials.addSheet.tooLarge'));
      setFile(null);
      return;
    }
    setFile(picked);
    if (!title) setTitle(picked.name.replace(/\.[^.]+$/, ''));
  };

  /** Registers the material, then sends the bytes to the presigned target. */
  const publishFile = async () => {
    if (!file || !category || !title.trim()) return;
    setSaving(true);
    try {
      const { material, upload } = await LaunchesService.materialUploadLink(agencyId, launchId, {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        category,
        title: title.trim(),
      });
      const target = upload as { url: string; fields?: Record<string, string> };
      const form = new FormData();
      Object.entries(target.fields ?? {}).forEach(([k, v]) => form.append(k, v));
      form.append('file', file);
      const response = await fetch(target.url, { method: 'POST', body: form });
      if (!response.ok) throw new Error(String(response.status));
      await publishVersionIfPriceTable(material);
      reset(); onCreated(); onClose();
    } catch {
      setUploadError(t('materials.addSheet.uploadFailed'));
    } finally {
      setSaving(false);
    }
  };

  const resolve = (value: string) => {
    setUrl(value);
    setResolved(null);
    if (!value.trim()) return;
    setResolving(true);
    LaunchesService.resolveLink(agencyId, launchId, value.trim())
      .then((outcome) => {
        setResolved(outcome);
        if (outcome.provider_title && !title) setTitle(outcome.provider_title);
      })
      .finally(() => setResolving(false));
  };

  const canPublish = mode === 'link'
    ? Boolean(resolved?.embed_status === 'ok' && title.trim() && category && !saving)
    : Boolean(file && title.trim() && category && !saving);

  /**
   * A price table is a versioned object, not a file in a folder: the
   * category screen reads /price-tables, so a material filed under
   * price_table without a version behind it shows up in the category count
   * and nowhere else — saved, invisible, unopenable.
   */
  const publishVersionIfPriceTable = async (material: { material_id: string }) => {
    if (category !== 'price_table') return material;
    await LaunchesService.publishPriceTable(agencyId, launchId, material.material_id);
    return material;
  };

  const publish = () => {
    if (!canPublish) return;
    if (mode === 'file') { void publishFile(); return; }
    if (!resolved) return;
    setSaving(true);
    LaunchesService.createEmbedMaterial(agencyId, launchId, {
      url: resolved.normalized_url,
      category,
      title: title.trim(),
    })
      .then(publishVersionIfPriceTable)
      .then(() => { reset(); onCreated(); onClose(); })
      .finally(() => setSaving(false));
  };

  const body = (
      <div className="flex flex-col gap-[var(--space-4)]">
        <div className="flex gap-[var(--space-2)]">
          {(['link', 'file'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => { setMode(key); setUploadError(null); }}
              className="min-h-[var(--space-12)] flex-1 rounded-(--radius-md) border text-[length:var(--text-body-sm)]"
              style={{
                background: mode === key ? 'var(--surface-tertiary)' : 'var(--surface-primary)',
                borderColor: mode === key ? 'var(--border-strong)' : 'var(--border-subtle)',
                color: 'var(--text-primary)',
              }}
            >
              {t(`materials.addSheet.mode.${key}`)}
            </button>
          ))}
        </div>

        {mode === 'file' && (
          <label className="flex flex-col gap-[var(--space-2)]">
            <span className="flex min-h-[var(--space-20)] cursor-pointer flex-col items-center justify-center gap-[var(--space-2)] rounded-(--radius-md) border border-dashed border-[var(--border-medium)] p-[var(--space-4)] text-center">
              <Upload size={20} color="var(--text-tertiary)" />
              <Text variant="bodySm" as="span" className="text-[var(--text-secondary)]">
                {file ? file.name : t('materials.addSheet.dropzone')}
              </Text>
              <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
                {t('materials.addSheet.accepted')}
              </Text>
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
            </span>
            {uploadError && (
              <Text
                as="span"
                className="text-[length:var(--text-meta)]"
                style={{ color: 'var(--launch-danger-fg)' }}
              >
                {uploadError}
              </Text>
            )}
          </label>
        )}

        {mode === 'link' && (
        <label className="flex flex-col gap-[var(--space-2)]">
          <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
            {t('materials.addSheet.pasteLink')}
          </Text>
          <span className="flex min-h-[var(--space-12)] items-center gap-[var(--space-2)] rounded-(--radius-md) border border-[var(--border-subtle)] px-[var(--space-3)]">
            <Link2 size={16} color="var(--text-tertiary)" />
            <input
              value={url}
              onChange={(e) => resolve(e.target.value)}
              placeholder="https://"
              className="w-full bg-transparent text-[length:var(--text-body-sm)] text-[var(--text-primary)] outline-none"
            />
          </span>
          {!url && (
            <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
              {t('materials.addSheet.acceptedProviders')}
            </Text>
          )}
        </label>
        )}

        {mode === 'link' && resolving && (
          <Text
            as="span"
            data-testid="link-identifying"
            className="flex items-center gap-[var(--space-2)] text-[length:var(--text-small)] text-[var(--text-secondary)]"
          >
            <AlertTriangle size={14} />
            {t('materials.addSheet.identifying')}
          </Text>
        )}

        {mode === 'link' && resolved && !resolving && <LinkOutcome resolved={resolved} />}

        {(mode === 'file' ? Boolean(file) : resolved?.embed_status === 'ok') && (
          <>
            <label className="flex flex-col gap-[var(--space-2)]">
              <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
                {t('materials.addSheet.titleLabel')}
              </Text>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="min-h-[var(--space-12)] rounded-(--radius-md) border border-[var(--border-subtle)] px-[var(--space-3)] text-[length:var(--text-body-sm)] text-[var(--text-primary)]"
              />
            </label>

            <div className="flex flex-col gap-[var(--space-2)]">
              <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
                {t('materials.addSheet.categoryLabel')}
              </Text>
              <div className="grid grid-cols-2 gap-[var(--space-2)]">
                {categories.map((c) => (
                  <button
                    key={c.category_key}
                    type="button"
                    onClick={() => setCategory(c.category_key)}
                    className="min-h-[var(--space-12)] rounded-(--radius-md) border px-[var(--space-2)] text-[length:var(--text-meta)]"
                    style={{
                      borderColor: category === c.category_key
                        ? 'var(--seed-base)'
                        : 'var(--border-subtle)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {c.fixed ? t(`materials.categories.${c.category_key}`) : c.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Publishing a file is two round trips plus the bytes themselves,
            which on a 40MB PDF is long enough that a button sitting there
            unchanged reads as a screen that froze. The label is what tells
            the manager the upload is running and not to paste it again. */}
        <button
          type="button"
          disabled={!canPublish}
          onClick={publish}
          data-testid="publish-material"
          aria-busy={saving}
          className="flex min-h-[var(--space-12)] w-full items-center justify-center gap-[var(--space-2)] rounded-(--radius-md) text-[length:var(--text-body-sm)]"
          style={{
            background: 'var(--seed-base)',
            color: 'var(--text-inverse)',
            opacity: canPublish ? undefined : 'var(--launch-opacity-disabled)',
          }}
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          {saving
            ? t(`materials.addSheet.${mode === 'file' ? 'uploading' : 'publishing'}`)
            : t('materials.addSheet.publish')}
        </button>
      </div>
  );

  // Closing mid-publish would abandon an upload already in flight, and the
  // material row the server has by then. The sheet holds until it lands.
  const close = () => { if (saving) return; reset(); onClose(); };

  // The phone gets a sheet, the desktop a 600px dialog — the pattern the
  // rest of the app uses for this kind of form, and what the handoff asked
  // for. A full-width bottom sheet on a 1280px screen reads as a different
  // product.
  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        title={t('materials.addSheet.title')}
        onClose={close}
        testId="add-material-sheet"
      >
        {body}
      </MobileBottomSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent data-testid="add-material-sheet" className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('materials.addSheet.title')}</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
